<template>
    <div class="listener-queue">
        <div class="listener-queue__header">
            <div>
                <div class="listener-queue__eyebrow">{{ $tt('播放队列') }}</div>
                <h1 class="listener-queue__title">{{ $tt('继续聆听') }}</h1>
            </div>
            <button class="listener-queue__device" @click="goGroupCast" v-if="tracks.length > 0">
                <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 7h5v2H7zm0 4h10v2H7zm0 4h7v2H7z"></path>
                    <path d="M17 8V5l4 4-4 4v-3h-3V8zM17 19v-3h-3v-2h3v-3l4 4-4 4z"></path>
                </svg>
                <span>{{ $tt('同播') }}</span>
            </button>
        </div>

        <div v-if="currentTrack" class="listener-queue__current" @click="goNowPlaying">
            <img
                :src="currentTrack.image || '/static/dist/img/album_null.jpg'"
                :alt="currentTrack.title"
                class="listener-queue__current-cover"
            >
            <div class="listener-queue__current-meta">
                <div class="listener-queue__current-label">{{ $tt('当前播放') }}</div>
                <div class="listener-queue__current-title">{{ currentTrack.title }}</div>
                <div class="listener-queue__current-artist">{{ currentTrack.artist || currentTrack.album }}</div>
            </div>
            <button class="listener-queue__current-play" @click.stop="togglePlay">
                <svg v-if="!isPlaying" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l10-6.86a1 1 0 0 0 0-1.72l-10-6.86A1 1 0 0 0 8 5.14z"></path>
                </svg>
                <svg v-else viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 5h3v14H7zm7 0h3v14h-3z"></path>
                </svg>
            </button>
        </div>

        <div class="listener-queue__actions" v-if="tracks.length > 0">
            <button class="listener-queue__action" @click="toggleShuffle">
                <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17 3h4v4h-2V6.41l-3.29 3.3-1.42-1.42L17.59 5H17V3zM3 7h5l3.29 3.29-1.42 1.42L7.17 9H3V7zm18 10v4h-4v-2h.59l-3.3-3.29 1.42-1.42 3.29 3.3V17H21zM3 15h4.17l10-10H21V3h-4v2h.59L7.17 15H3v2z"></path>
                </svg>
                <span>{{ $tt('打乱') }}</span>
            </button>
            <button class="listener-queue__action" @click="playRandom">
                <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2a10 10 0 0 0-7.07 17.07A10 10 0 1 0 19.07 4.93 9.93 9.93 0 0 0 12 2zm0 5a1 1 0 0 1 1 1v3h3a1 1 0 1 1 0 2h-4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1z"></path>
                </svg>
                <span>{{ $tt('随机') }}</span>
            </button>
            <button class="listener-queue__action" @click="clearQueue">
                <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v8h-2V9zm4 0h2v8h-2V9zM7 9h2v8H7V9z"></path>
                </svg>
                <span>{{ $tt('清空') }}</span>
            </button>
        </div>

        <div v-if="tracks.length > 0" class="listener-queue__list">
            <div
                v-for="(item, index) in tracks"
                :key="`${item.id || 'queue'}-${index}`"
                :class="['listener-queue__row', index === queueIndex ? 'is-current' : '']"
                @click="playAt(index)"
            >
                <div class="listener-queue__row-index">{{ index + 1 }}</div>
                <img
                    :src="item.image || '/static/dist/img/album_null.jpg'"
                    :alt="item.title"
                    class="listener-queue__row-cover"
                >
                <div class="listener-queue__row-meta">
                    <div class="listener-queue__row-title">{{ item.title }}</div>
                    <div class="listener-queue__row-artist">{{ item.artist || item.album }}</div>
                </div>
                <button class="listener-queue__row-button" @click.stop="removeAt(index)">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7 11h10v2H7z"></path>
                    </svg>
                </button>
            </div>
        </div>

        <div v-else class="listener-queue__empty">
            <div class="listener-queue__empty-text">{{ $tt('暂无播放') }}</div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
    name: 'ListenerQueueDetails',
    computed: {
        ...mapGetters(['track']),
        tracks() {
            return this.$store.state.common.queue || []
        },
        queueIndex() {
            return this.$store.state.common.queueIndex
        },
        currentTrack() {
            return this.track
        },
        isPlaying() {
            return this.$store.state.common.isPlaying
        }
    },
    methods: {
        togglePlay() {
            this.$store.dispatch('playPause')
        },
        toggleShuffle() {
            this.$store.dispatch('toggleShuffle')
        },
        clearQueue() {
            this.$store.commit('clearQueue')
        },
        removeAt(index) {
            this.$store.commit('removeFromQueue', index)
        },
        playAt(index) {
            this.$store.dispatch('playTrackList', {
                tracks: this.tracks,
                index
            })
        },
        playRandom() {
            if (this.tracks.length === 0) return
            const index = Math.floor(Math.random() * this.tracks.length)
            this.playAt(index)
        },
        goGroupCast() {
            this.$router.push({ name: 'groupcast' })
        },
        goNowPlaying() {
            if (!this.currentTrack) return
            this.$router.push({ name: 'playingList', params: { id: this.currentTrack.id } })
        }
    }
}
</script>

<style scoped>
.listener-queue {
    height: calc(100dvh - 12.25rem);
    padding: 0.75rem 0.35rem 0.25rem;
    box-sizing: border-box;
    overflow-y: auto;
}

.listener-queue__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
}

.listener-queue__eyebrow {
    font-size: 0.8rem;
    font-weight: 700;
    color: #94a3b8;
}

.listener-queue__title {
    margin-top: 0.18rem;
    font-size: 2rem;
    line-height: 1;
    font-weight: 800;
    color: #111827;
    letter-spacing: -0.04em;
}

.listener-queue__device {
    border: 0;
    min-height: 2.8rem;
    padding: 0 0.95rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.88);
    color: #111827;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.38rem;
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.15);
    font-size: 0.78rem;
    font-weight: 700;
    flex-shrink: 0;
}

.listener-queue__device svg {
    width: 1.05rem;
    height: 1.05rem;
}

.listener-queue__current {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    padding: 0.95rem;
    border-radius: 1.7rem;
    background: linear-gradient(135deg, rgba(17, 24, 39, 0.96), rgba(31, 41, 55, 0.92));
    color: #fff;
    box-shadow: 0 20px 46px rgba(15, 23, 42, 0.18);
}

.listener-queue__current-cover {
    width: 4rem;
    height: 4rem;
    object-fit: cover;
    border-radius: 1rem;
    flex-shrink: 0;
}

.listener-queue__current-meta {
    min-width: 0;
    flex: 1;
}

.listener-queue__current-label {
    font-size: 0.72rem;
    color: rgba(255,255,255,0.65);
}

.listener-queue__current-title,
.listener-queue__current-artist,
.listener-queue__row-title,
.listener-queue__row-artist {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-queue__current-title {
    margin-top: 0.25rem;
    font-size: 1rem;
    font-weight: 700;
}

.listener-queue__current-artist {
    margin-top: 0.2rem;
    font-size: 0.78rem;
    color: rgba(255,255,255,0.68);
}

.listener-queue__current-play {
    width: 2.85rem;
    height: 2.85rem;
    border: 0;
    border-radius: 999px;
    background: #fff;
    color: #111827;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.listener-queue__current-play svg {
    width: 1.25rem;
    height: 1.25rem;
}

.listener-queue__actions {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.7rem;
    margin-top: 1rem;
}

.listener-queue__action {
    border: 0;
    border-radius: 1.2rem;
    background: rgba(255,255,255,0.8);
    color: #111827;
    padding: 0.8rem 0.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.35rem;
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.1);
    font-size: 0.75rem;
    font-weight: 700;
}

.listener-queue__action svg {
    width: 1.2rem;
    height: 1.2rem;
}

.listener-queue__list {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.listener-queue__row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.8rem 0.7rem;
    border-radius: 1.35rem;
    background: rgba(255,255,255,0.74);
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.08);
}

.listener-queue__row.is-current {
    background: linear-gradient(135deg, rgba(255,255,255,0.95), rgba(226,232,240,0.86));
    box-shadow: 0 16px 34px rgba(148, 163, 184, 0.14);
}

.listener-queue__row-index {
    width: 1.2rem;
    font-size: 0.78rem;
    font-weight: 700;
    color: #94a3b8;
    text-align: center;
    flex-shrink: 0;
}

.listener-queue__row-cover {
    width: 3.15rem;
    height: 3.15rem;
    object-fit: cover;
    border-radius: 0.95rem;
    flex-shrink: 0;
}

.listener-queue__row-meta {
    min-width: 0;
    flex: 1;
}

.listener-queue__row-title {
    font-size: 0.94rem;
    font-weight: 700;
    color: #111827;
}

.listener-queue__row-artist {
    margin-top: 0.2rem;
    font-size: 0.76rem;
    color: #94a3b8;
}

.listener-queue__row-button {
    width: 2rem;
    height: 2rem;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #94a3b8;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.listener-queue__row-button svg {
    width: 1rem;
    height: 1rem;
}

.listener-queue__empty {
    padding-top: 4rem;
    text-align: center;
    color: #94a3b8;
}

.listener-queue__empty-image {
    width: 10rem;
    margin: 0 auto 1rem;
}

.listener-queue__empty-text {
    font-size: 0.92rem;
    font-weight: 700;
}
</style>
